/**
 * Date Formatting Utilities
 *
 * Provides consistent date formatting and relative time descriptions
 * across the application with enhanced handling for longer time periods.
 */

import dayjs from 'dayjs';
import pluralize from 'pluralize';
import { date } from 'quasar';

/**
 * Formats a relative time description based on the difference in days
 *
 * @param days - Number of days difference (positive for future, negative for past)
 * @returns Formatted relative time string
 */
export function formatRelativeTime(days: number): string {
  const absDays = Math.abs(days);
  const isFuture = days > 0;

  if (absDays === 0) {
    return 'today';
  } else if (absDays === 1) {
    return isFuture ? 'tomorrow' : 'yesterday';
  } else if (absDays < 90) {
    return isFuture ? `in ${absDays} days` : `${absDays} days ago`;
  } else if (absDays < 365) {
    const months = Math.floor(absDays / 30.44); // Average days per month, rounded down
    const monthText = months === 1 ? 'month' : 'months';
    return isFuture
      ? `in ${months} ${monthText}`
      : `${months} ${monthText} ago`;
  } else {
    const diffYears = absDays / 365.25; // Account for leap years
    const wholeYears = Math.floor(diffYears);
    const fractionalPart = diffYears - wholeYears;
    const fractionalMonths = fractionalPart * 12;

    let displayYears: string;

    if (fractionalMonths < 4) {
      // Round down
      displayYears = wholeYears.toString();
    } else if (fractionalMonths > 8) {
      // Round up
      displayYears = (wholeYears + 1).toString();
    } else {
      // Use half symbol (4-8 months)
      displayYears = `${wholeYears}½`;
    }

    // Check for half symbol to ensure plural form
    const hasHalfSymbol = displayYears.includes('½');
    const isPlural = displayYears !== '1' || hasHalfSymbol;
    const yearText = isPlural ? 'years' : 'year';

    return isFuture
      ? `in ~${displayYears} ${yearText}`
      : `~${displayYears} ${yearText} ago`;
  }
}

/**
 * Gets relative time description for a given date string
 *
 * @param dateString - ISO date string
 * @returns Relative time description or empty string if invalid date
 */
export function getRelativeTime(dateString: string | null): string {
  if (!dateString) return '';

  try {
    const targetDate = new Date(dateString);
    const now = new Date();

    // Validate date
    if (isNaN(targetDate.getTime())) return '';

    const diffDays = date.getDateDiff(targetDate, now, 'days');
    return formatRelativeTime(diffDays);
  } catch (error) {
    console.warn(
      'Invalid date string provided to getRelativeTime:',
      dateString
    );
    return '';
  }
}

/**
 * Formats a date using British format (25 Nov 2025, excluding year if current year)
 *
 * @param dateString - ISO date string
 * @returns Formatted date string or empty string if invalid
 */
export function formatDateGB(dateString: string | null): string {
  if (!dateString) return '';

  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '';

    const currentYear = new Date().getFullYear();
    const dateYear = date.getFullYear();
    const includeYear = dateYear !== currentYear;

    const day = date.getDate();
    const monthNames = [
      'Jan',
      'Feb',
      'Mar',
      'Apr',
      'May',
      'Jun',
      'Jul',
      'Aug',
      'Sep',
      'Oct',
      'Nov',
      'Dec',
    ];
    const month = monthNames[date.getMonth()];

    return includeYear ? `${day} ${month} ${dateYear}` : `${day} ${month}`;
  } catch (error) {
    console.warn('Invalid date string provided to formatDateGB:', dateString);
    return '';
  }
}

/**
 * Formats a date with relative time description below
 *
 * @param dateString - ISO date string
 * @returns Object with formatted date and relative time
 */
export function formatDateWithRelative(dateString: string | null): {
  date: string;
  relative: string;
} {
  return {
    date: formatDateGB(dateString),
    relative: getRelativeTime(dateString),
  };
}

/**
 * Formats the time portion of a date string in HH:mm format.
 *
 * @param dateString - ISO date string
 * @returns Formatted time string (e.g. "14:32") or empty string if invalid
 */
export function formatTimeGB(dateString: string | null): string {
  if (!dateString) return '';

  try {
    const parsedDate = new Date(dateString);
    if (isNaN(parsedDate.getTime())) return '';

    return date.formatDate(parsedDate, 'HH:mm');
  } catch (error) {
    console.warn('Invalid date string provided to formatTimeGB:', dateString);
    return '';
  }
}

/**
 * Formats a date with full British format including time
 *
 * @param dateString - ISO date string
 * @returns Formatted date string (DD/MM/YYYY HH:mm:ss) or empty string if invalid
 */
export function formatFullDateTime(dateString: string | null): string {
  if (!dateString) return '';

  try {
    return date.formatDate(new Date(dateString), 'DD/MM/YYYY HH:mm:ss');
  } catch (error) {
    console.warn(
      'Invalid date string provided to formatFullDateTime:',
      dateString
    );
    return 'Invalid date';
  }
}

/**
 * Gets a human-readable time ago description for shorter periods
 * Falls back to formatRelativeTime for longer periods
 *
 * @param dateString - ISO date string
 * @returns Human-readable time description
 */
export function getTimeAgo(dateString: string | null): string {
  if (!dateString) return '';

  try {
    const targetDate = new Date(dateString);
    const now = new Date();

    if (isNaN(targetDate.getTime())) return '';

    const diffMinutes = date.getDateDiff(targetDate, now, 'minutes');
    const diffHours = date.getDateDiff(targetDate, now, 'hours');
    const diffDays = date.getDateDiff(targetDate, now, 'days');

    // For very recent times, use precise descriptions
    if (Math.abs(diffMinutes) < 1) {
      return 'just now';
    } else if (Math.abs(diffMinutes) < 60) {
      const absMinutes = Math.abs(diffMinutes);
      return diffMinutes > 0
        ? `in ${absMinutes} minute${absMinutes === 1 ? '' : 's'}`
        : `${absMinutes} minute${absMinutes === 1 ? '' : 's'} ago`;
    } else if (Math.abs(diffHours) < 24) {
      const absHours = Math.abs(diffHours);
      return diffHours > 0
        ? `in ${absHours} hour${absHours === 1 ? '' : 's'}`
        : `${absHours} hour${absHours === 1 ? '' : 's'} ago`;
    } else {
      // For longer periods, use the enhanced relative time formatter
      return formatRelativeTime(diffDays);
    }
  } catch (error) {
    console.warn('Invalid date string provided to getTimeAgo:', dateString);
    return '';
  }
}

/**
 * Calculates the duration between two dates in a human-readable format
 *
 * @param startDate - Start date string (ISO format)
 * @param endDate - End date string (ISO format), or null for open-ended
 * @returns Human-readable duration string (e.g., "3 months", "1 year 2 months", "Open-ended")
 */
/**
 * Break the span between two dates into whole calendar years, months and days,
 * borrowing across month and year boundaries the way a person would (rather
 * than approximating with an average 30.44-day month). Assumes `end >= start`.
 */
function getCalendarParts(
  start: Date,
  end: Date
): { years: number; months: number; days: number } {
  let years = end.getFullYear() - start.getFullYear();
  let months = end.getMonth() - start.getMonth();
  let days = end.getDate() - start.getDate();

  if (days < 0) {
    months -= 1;
    // Borrow the number of days in the calendar month preceding the end date.
    const daysInBorrowedMonth = new Date(
      end.getFullYear(),
      end.getMonth(),
      0
    ).getDate();
    days += daysInBorrowedMonth;
  }

  if (months < 0) {
    years -= 1;
    months += 12;
  }

  return { years, months, days };
}

export function getDateDuration(
  startDate: string | null | undefined,
  endDate: string | null | undefined
): string {
  if (!startDate) return '';

  if (!endDate) return 'Open-ended';

  try {
    const start = new Date(startDate);
    const end = new Date(endDate);

    if (isNaN(start.getTime()) || isNaN(end.getTime())) return '';

    // The validity end date is inclusive — the rule is valid through the whole
    // of the end day — so measure to the day after it. This makes a full
    // calendar range such as 1 Jan → 31 Dec report as exactly "1 year".
    const inclusiveEnd = date.addToDate(end, { days: 1 });

    const diffDays = Math.abs(date.getDateDiff(inclusiveEnd, start, 'days'));

    if (diffDays <= 1) return '1 day';
    if (diffDays < 7) return `${diffDays} days`;

    // For anything a week or longer, use calendar-aware year/month/day parts so
    // durations track real months rather than a fixed-length approximation.
    const { years, months, days } = getCalendarParts(start, inclusiveEnd);

    if (years === 0 && months === 0) {
      // Under a month — express in weeks plus any remaining days.
      const weeks = Math.floor(days / 7);
      const remainingDays = days % 7;
      const weekPart = weeks === 1 ? '1 week' : `${weeks} weeks`;
      if (remainingDays === 0) return weekPart;
      return `${weekPart} ${remainingDays} day${
        remainingDays === 1 ? '' : 's'
      }`;
    }

    if (years === 0) {
      const monthPart = months === 1 ? '1 month' : `${months} months`;
      if (days < 7) return monthPart;
      const weeks = Math.floor(days / 7);
      const weekPart = weeks === 1 ? '1 week' : `${weeks} weeks`;
      return `${monthPart} ${weekPart}`;
    }

    const yearPart = years === 1 ? '1 year' : `${years} years`;
    if (months === 0) return yearPart;
    const monthPart = months === 1 ? '1 month' : `${months} months`;
    return `${yearPart} ${monthPart}`;
  } catch (error) {
    console.warn('Invalid date strings provided to getDateDuration:', {
      startDate,
      endDate,
    });
    return '';
  }
}
export function formatElapsedTime(startDate: string, endDate?: string) {
  const fromDate = dayjs(startDate);
  const currentDate = endDate ? dayjs(endDate) : dayjs();

  const years = currentDate.diff(fromDate, 'year');
  const months = currentDate.subtract(years, 'year').diff(fromDate, 'month');
  const yearsLabel = pluralize('year', years, true);
  const monthsLabel = pluralize('month', months, true);

  if (years === 0 && months === 0) {
    return 'Less than a month';
  } else if (years === 0) {
    return monthsLabel;
  } else if (months === 0) {
    return yearsLabel;
  }
  return `${yearsLabel} and ${monthsLabel}`;
}
